import './index.less';

import { Button, Divider, message } from 'antd';
import { nanoid } from 'nanoid';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DownloadOutlined } from '@ant-design/icons';

import { FileTypeEnum } from '../../../enum/fileType';
import {
  getApplicationExperianReportThunk,
  regenerateApplicationFormThunk,
} from '../../../services/applicationService/applicationThunk';
import { getWiseAiResultByApplicationIdThunk } from '../../../services/ekycService/ekycThunk';
import {
  getEkycFilesByEntityIdThunk,
  getFileByIdThunk,
  getFilesByEntityIdThunk,
  getRefinanceFilesByApplicationIdThunk,
} from '../../../services/fileService/fileThunk';
import { AppDispatch, RootState } from '../../../store/store';
import UploadedDocument from '../../UploadedDocument';
import { ApplicationStatusEnum } from '../../../enum/applicationStepStatus';
import { Refinance } from '../../../features/refinance/refinanceSlice';
import { Application } from '../../../features/application/applicationSlice';

interface AttachmentTabProps {
  applicationData?: Application;
  ekycFilesData?: any;
}

const AttachmentTab = ({ applicationData, ekycFilesData }: AttachmentTabProps) => {
  const [experianReportType, setExperianReportType] = useState<string>('');
  const [applicationFormFileList, setApplicationFormFileList] = useState<
    {
      id: string | null;
      filename: string;
    }[]
  >([]);
  const [experianReportFileList, setExperianReportFileList] = useState<
    {
      id: string | null;
      filename: string;
    }[]
  >([]);
  const [refinanceFileList, setRefinanceFileList] = useState<
    {
      id: string | null;
      filename: string;
    }[]
  >([]);
  const dispatch = useDispatch<AppDispatch>();
  const { applicationDetails, ekycFiles } = useSelector((state: RootState) => state.application);

  // Use props if available, otherwise fall back to Redux state
  const currentApplicationData = applicationData || applicationDetails?.data;
  const currentEkycFiles = ekycFilesData || ekycFiles;

  console.log('AttachmentTab - applicationData:', applicationData);
  console.log('AttachmentTab - ekycFilesData:', ekycFilesData);
  console.log('AttachmentTab - currentEkycFiles:', currentEkycFiles);

  const getExperianHTMLReport = (type: 'IRISS' | 'SPKCCS' | 'NRVB' | 'AMLA', name: string) => {
    const fileId =
      type === 'NRVB'
        ? currentApplicationData?.experianNvrbFileId
        : type === 'AMLA'
        ? currentApplicationData?.experianAmlaFileId
        : type === 'IRISS'
        ? currentApplicationData?.experianIrissFileId
        : currentApplicationData?.experianSpkccsFileId;

    dispatch(
      getApplicationExperianReportThunk({
        fileId: fileId,
        newIc: currentApplicationData?.applicantNric,
        params: { type },
      })
    )
      .unwrap()
      .then((res) => {
        // const htmlReport = parse(res);
        const newWindow = window.open('', '_blank');
        if (newWindow != null) {
          newWindow.document.title = name;
          newWindow.document.write(res);
          newWindow.document.close();
        }
      });
  };

  const onGenerateApplicationForm = () => {
    dispatch(
      regenerateApplicationFormThunk({
        applicationId: currentApplicationData?.id,
      })
    )
      .unwrap()
      .then((res) => {
        // todo check if response return file id
        // now dont have response yet
        // for now need refresh after 10 seconds to get the new application form
        // const data = {
        //   id: res?.id,
        //   filename: "Application Form",
        // };
        // setApplicationFormFileList([data]);
        message.success('Application form generated successfully');
      });
  };

  useEffect(() => {
    // Only run Redux dispatches if we're not using props (i.e., we're in the main page)
    if (!applicationData && !ekycFilesData) {
      // ekyc files
      dispatch(
        getWiseAiResultByApplicationIdThunk({
          applicationId: currentApplicationData?.id,
        })
      )
        .unwrap()
        .then((res) => {
          if (res?.length > 0 && res[0] != null) {
            dispatch(getEkycFilesByEntityIdThunk({ entityId: res[0]?.id }));
          }
        });
    }

    // application form
    if (currentApplicationData?.applicationFormFileId) {
      const data = {
        id: currentApplicationData?.applicationFormFileId,
        filename: 'Application Form',
      };
      setApplicationFormFileList([data]);
    }

    // experian report
    if (
      currentApplicationData?.experianIrissFileId ||
      currentApplicationData?.experianSpkccsFileId
    ) {
      const reportId =
        currentApplicationData?.experianIrissFileId || currentApplicationData?.experianSpkccsFileId;

      const data = {
        id: currentApplicationData?.id ?? '',
        filename: currentApplicationData?.experianIrissFileId
          ? 'Experian IRISS Report'
          : 'Experian SPKCCS Report',
      };
      setExperianReportFileList([data]);

      setExperianReportType(currentApplicationData?.experianIrissFileId ? 'IRISS' : 'SPKCCS');
    }

    if (currentApplicationData?.refinance) {
      dispatch(
        getRefinanceFilesByApplicationIdThunk({
          applicationId: currentApplicationData.id,
        })
      )
        .unwrap()
        .then((res) => {
          setRefinanceFileList(res);
        });
    }
  }, [currentApplicationData?.userId, applicationData, ekycFilesData]);

  return (
    <div className="info-container">
      {currentEkycFiles?.data?.length > 0 && (
        <>
          <UploadedDocument
            key={'1'}
            description="NRIC Front & Back"
            defaultFileList={currentEkycFiles?.data}
            loading={currentEkycFiles?.isLoading}
            readOnly
            fileType={FileTypeEnum.EKYCFILES}
          />
          <Divider className="my-4" />
        </>
      )}

      <>
        <UploadedDocument
          key={'2'}
          description="NRVB Report"
          defaultFileList={[
            {
              id: currentApplicationData?.id ?? '',
              filename: 'Experian NRVB Report',
            },
          ]}
          readOnly
          fileType={FileTypeEnum.NRVB}
          customDownloadButton={
            <Button
              key={nanoid()}
              icon={<DownloadOutlined />}
              onClick={() => getExperianHTMLReport('NRVB', 'Experian NRVB Report')}
            >
              Download
            </Button>
          }
          fileId={currentApplicationData?.experianNvrbFileId}
          newIc={currentApplicationData?.applicantNric}
        />

        <Divider className="my-4" />
      </>

      {currentApplicationData?.experianAmlaFileId && (
        <>
          <UploadedDocument
            key={'2'}
            description="AMLA Report"
            defaultFileList={[
              {
                id: currentApplicationData?.id ?? '',
                filename: 'Experian AMLA Report',
              },
            ]}
            readOnly
            fileType={FileTypeEnum.AMLA}
            customDownloadButton={
              <Button
                key={nanoid()}
                icon={<DownloadOutlined />}
                onClick={() => getExperianHTMLReport('AMLA', 'Experian AMLA Report')}
              >
                Download
              </Button>
            }
            fileId={currentApplicationData?.experianAmlaFileId}
            newIc={currentApplicationData?.applicantNric}
          />
          <Divider className="my-4" />
        </>
      )}

      {experianReportType === 'IRISS' && (
        <>
          <UploadedDocument
            key={'2'}
            description={`IRISS Report (${currentApplicationData?.numberOfCcrisReport})`}
            defaultFileList={experianReportFileList}
            readOnly
            fileType={FileTypeEnum.IRISS}
            customDownloadButton={
              <Button
                key={nanoid()}
                icon={<DownloadOutlined />}
                onClick={() => getExperianHTMLReport('IRISS', 'Experian IRISS Report')}
              >
                Download
              </Button>
            }
            fileId={currentApplicationData?.experianIrissFileId}
            newIc={currentApplicationData?.applicantNric}
          />
          <Divider className="my-4" />
        </>
      )}

      {experianReportType === 'SPKCCS' && (
        <>
          <UploadedDocument
            key={'3'}
            description={`SPKCCS Report (${currentApplicationData?.numberOfCcrisReport})`}
            defaultFileList={experianReportFileList}
            readOnly
            fileType={FileTypeEnum.SPKCCS}
            customDownloadButton={
              <Button
                key={nanoid()}
                icon={<DownloadOutlined />}
                onClick={() => getExperianHTMLReport('SPKCCS', 'Experian SPKCCS Report')}
              >
                Download
              </Button>
            }
            fileId={currentApplicationData?.experianSpkccsFileId}
            newIc={currentApplicationData?.applicantNric}
          />
          <Divider className="my-4" />
        </>
      )}

      {refinanceFileList?.length > 0 && (
        <>
          <UploadedDocument
            key={'4'}
            description="Refinance Supporting Documents"
            defaultFileList={refinanceFileList}
            readOnly
            fileType={FileTypeEnum.REFINANCE_SUPPORTING_DOCUMENTS}
          />
          <Divider className="my-4" />
        </>
      )}

      {applicationFormFileList.length > 0 && (
        <>
          <UploadedDocument
            key={'5'}
            description="Application Form"
            defaultFileList={applicationFormFileList}
            readOnly
            fileType={FileTypeEnum.APPLICATIONFILES}
          />

          <Button key={nanoid()} className="my-3" onClick={() => onGenerateApplicationForm()}>
            Regenerate Application Form
          </Button>
        </>
      )}
      {applicationFormFileList.length <= 0 &&
        currentApplicationData?.applicationStatus !== ApplicationStatusEnum.DRAFT && (
          <>
            <h4>Application Form</h4>
            <Button key={nanoid()} className="my-3" onClick={() => onGenerateApplicationForm()}>
              Generate Application Form
            </Button>
          </>
        )}
    </div>
  );
};

export default AttachmentTab;
